import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { LockKeyhole, Loader2, Mail } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { AuthShell } from '../components/auth/AuthShell';
import { EmailLinkRequest } from '../components/auth/EmailLinkRequest';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { authErrorMessage, safeAuthDestination } from '../utils/authNavigation';

export default function Login() {
  const [params] = useSearchParams();
  const destination = safeAuthDestination(params.get('next'));
  const hasDraft = destination.startsWith('/events/') && ['A', 'B'].includes(new URLSearchParams(destination.split('?')[1] || '').get('draft'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordMode, setPasswordMode] = useState(params.get('method') === 'password');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const submitPassword = async event => {
    event.preventDefault();
    if (busy) return;
    setBusy(true); setError('');
    try {
      const user = await login(email.trim(), password);
      navigate(user.role === 'admin' && !params.has('next') ? '/admin' : destination, { replace: true });
    } catch (err) {
      setError(authErrorMessage(err, 'Unable to sign in. Please check your details.'));
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title={hasDraft ? "Make your vote count" : "Sign in to participate"} titleId="login-title" description={passwordMode ? 'Use your existing account password.' : hasDraft ? 'Join or sign in with an email link. No password needed.' : 'Your next prediction starts here.'}>
      {destination.startsWith('/events/') && <p className="text-sm text-indigo-300 mb-5" data-testid="login-return-context">Your selected question will be waiting after sign-in. You’ll review and confirm your answer before it is saved.</p>}
      <EmailLinkRequest destination={destination} email={email} setEmail={setEmail} passwordMode={passwordMode}>
        {passwordMode && <form id="password-login-form" onSubmit={submitPassword} className="space-y-5" data-testid="password-login-form">
          <div>
            <Label htmlFor="login-password" className="text-slate-300" data-testid="login-password-label">Password</Label>
            <Input id="login-password" type="password" required autoComplete="current-password" value={password} onChange={event => setPassword(event.target.value)} className="mt-2 h-12 bg-slate-950 border-slate-700 text-white" data-testid="login-password-input" />
          </div>
          <Link to="/forgot-password" className="block text-sm text-indigo-300 hover:text-indigo-200" data-testid="forgot-password-link">Forgot password?</Link>
          {error && <p role="alert" className="text-sm text-rose-300" data-testid="password-login-error">{error}</p>}
          <Button type="submit" disabled={busy} className="h-12 w-full rounded-full bg-indigo-600 hover:bg-indigo-500" data-testid="login-submit-button">{busy && <Loader2 className="w-4 h-4 animate-spin" />}{busy ? 'Signing in…' : 'Sign in with password'}</Button>
          <Link to={`/signup?next=${encodeURIComponent(destination)}`} className="block text-center text-sm text-slate-300 hover:text-white" data-testid="login-signup-link">Create an account with a password</Link>
        </form>}
      </EmailLinkRequest>
      <div className="mt-8 pt-6 border-t border-slate-800">
        <Button variant="outline" type="button" onClick={() => { setPasswordMode(!passwordMode); setError(''); }} disabled={busy} className="w-full min-h-12 h-auto py-3 whitespace-normal border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 hover:text-white" aria-expanded={passwordMode} data-testid="login-method-toggle">
          {passwordMode ? <Mail className="w-4 h-4" /> : <LockKeyhole className="w-4 h-4" />}
          {passwordMode ? 'Use an email sign-in link' : 'Use password sign-in'}
        </Button>
      </div>
    </AuthShell>
  );
}